import { useEffect, useRef, useState } from "react";
import { Instagram, ArrowUpRight } from "lucide-react";

/**
 * InstagramEmbed
 * ---------------
 * Drop-in widget container for the homepage Instagram section.
 *
 * Vendor: Behold.so — renders a <behold-widget> custom element that the
 * Behold script upgrades into a live Instagram grid.
 *
 * Safe by design:
 *  - script is only loaded once per page (id-guarded against duplicates)
 *  - loaded only when this component mounts (will not run on /admin or PDP)
 *  - if script/widget never loads within FALLBACK_TIMEOUT_MS, the fallback
 *    card stays visible so the homepage never goes blank
 *  - never uses dangerouslySetInnerHTML
 */

// PASTE THIRD-PARTY INSTAGRAM WIDGET ID / SCRIPT URL HERE
// Vendor: Behold.so
const WIDGET_SCRIPT_URL = "https://w.behold.so/widget.js";
const BEHOLD_FEED_ID = "RFrmEdbXtdxRkY6rhcjN";
const WIDGET_SCRIPT_ID = "ose-instagram-widget-script";
const FALLBACK_TIMEOUT_MS = 6000;

const PROFILE_URL = "https://www.instagram.com/opensea_fishing_tackles/";

export default function InstagramEmbed() {
    const containerRef = useRef(null);
    const [widgetReady, setWidgetReady] = useState(false);

    useEffect(() => {
        if (!WIDGET_SCRIPT_URL) return undefined; // placeholder mode

        const existing = document.getElementById(WIDGET_SCRIPT_ID);

        // If script was already injected on a previous mount, the custom element
        // class is already registered — flip immediately so fallback doesn't flash.
        if (existing && window.customElements && window.customElements.get && window.customElements.get("behold-widget")) {
            setWidgetReady(true);
            return undefined;
        }

        let scriptEl = existing;
        if (!scriptEl) {
            scriptEl = document.createElement("script");
            scriptEl.id = WIDGET_SCRIPT_ID;
            scriptEl.src = WIDGET_SCRIPT_URL;
            scriptEl.type = "module";
            scriptEl.async = true;
            scriptEl.defer = true;
            document.head.appendChild(scriptEl);
        }

        const onLoad = () => setWidgetReady(true);
        const onError = () => {
            /* leave widgetReady=false → fallback stays */
        };
        scriptEl.addEventListener("load", onLoad);
        scriptEl.addEventListener("error", onError);

        // Belt-and-braces: if the script is cached and the load event has
        // already fired by the time we attach, watch for the custom element
        // upgrade by polling customElements.whenDefined.
        if (window.customElements && window.customElements.whenDefined) {
            window.customElements
                .whenDefined("behold-widget")
                .then(() => setWidgetReady(true))
                .catch(() => {});
        }

        const t = setTimeout(() => {
            // After timeout, if still not ready, fallback stays visible.
        }, FALLBACK_TIMEOUT_MS);

        return () => {
            clearTimeout(t);
            scriptEl.removeEventListener("load", onLoad);
            scriptEl.removeEventListener("error", onError);
        };
    }, []);

    return (
        <div className="w-full" data-testid="instagram-embed">
            {/* Vendor widget mounts inside this div.
               Behold.so: <behold-widget feed-id="..."/> custom element is
               upgraded by the script injected above. */}
            <div
                ref={containerRef}
                className="ig-widget-container w-full mx-auto max-w-full"
                style={WIDGET_SCRIPT_URL ? { minHeight: "240px" } : undefined}
            >
                {BEHOLD_FEED_ID && (
                    <behold-widget
                        feed-id={BEHOLD_FEED_ID}
                        data-testid="instagram-embed-behold"
                        style={{ display: "block", width: "100%" }}
                    />
                )}
            </div>

            {/* Fallback card — shown until the Behold script loads, or if it
                fails to load entirely. */}
            {!widgetReady && (
                <div
                    className="mt-6 mx-auto max-w-2xl text-center bg-[#121214] border border-dashed border-[#27272A] rounded-sm px-6 py-10"
                    data-testid="instagram-embed-fallback"
                >
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#0A0A0B] border border-[#27272A] mb-4">
                        <Instagram size={20} className="text-[#00BCE5]" />
                    </div>
                    <p className="text-white/70 text-sm leading-relaxed">
                        {WIDGET_SCRIPT_URL
                            ? "Loading the latest from Instagram…"
                            : "Instagram feed will appear here."}
                    </p>
                    <a
                        href={PROFILE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="instagram-embed-follow-btn"
                        className="inline-flex items-center gap-2 mt-5 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-5 py-2.5 rounded-sm hover:bg-[#33C9EA] transition-colors text-sm"
                    >
                        Follow us on Instagram <ArrowUpRight size={16} />
                    </a>
                </div>
            )}
        </div>
    );
}
